import { Form } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { IAdminMemberItem } from '..';
import {
  batchEditSupportRole,
  createSupportUsers,
  getSupportRoleList,
} from '../../../../../api/admin/rbac';
import { ConsoleDrawer } from '../../../../../components/drawer';
import { ConsoleForm } from '../../../../../components/form';
import {
  messageError,
  messageSuccess,
  messageWarning,
} from '../../../../../components/message';
import { CommonSelect } from '../../../../../components/select';
import getFormatMessage from '../../../../../locales/get-format-message';
import { DeployArea, RbacRoleType } from '../../../../../model';
import {
  IApiAdminRbacRole,
  ISupportUserModelCreate,
  IUserListItem,
} from '../../../../../model/api-modules';
import { useCallback, useState } from 'react';
import { getStaffList } from '../../../../../api';
import { debounce } from 'lodash';
import { generateStrongPassword } from '../../../../utils/password';
import { getConfig } from '../../../../../config';
import SparkMD5 from 'spark-md5';
import { ModalWrap } from '../../../../../components/modal-wrap';
import { CommonInput } from '../../../../../components/input';
import { NOTICE_ADMIN_ROLE } from '../../role';
import { CommonMultiSelect } from '../../../../../components/select/checkbox-select';

interface IInviteInfo {
  roles: number[];
  users: string;
}

interface IProps {
  readonly isSuper: boolean;
  readonly members: readonly IAdminMemberItem[];
  readonly allUser: readonly IUserListItem[];
  readonly roles: readonly IApiAdminRbacRole[];
  readonly onSave: () => void;
  readonly onCancel: () => void;
}

export const InviteDrawer = (props: IProps) => {
  const { isSuper, members, roles, onCancel, onSave } = props;
  const isSingaporeArea = getConfig().deployArea === DeployArea.singapore;
  const [staffOptions, setStaffOptions] = useState<
    { label: string; value: string; name: string }[]
  >([]);
  const [searchText, setSearchText] = useState<string>('');

  const searchStaffs = useCallback(
    debounce(async (searchText: string) => {
      if (!searchText) {
        setSearchText('');
        setStaffOptions([]);
        return;
      }
      const staffList = await getStaffList(searchText.toLowerCase());
      setStaffOptions(
        staffList.map((staff) => {
          return {
            label: staff.staffCombinedName,
            name: staff.staffDisplayName,
            value: `${staff.staffAccountName}`,
          };
        })
      );
      setSearchText(searchText);
    }, 200),
    []
  );

  const getInviteUserOptions = () => {
    const memberIds = members.map((item) => item.email);

    return staffOptions.map((item) => {
      return { ...item, disabled: memberIds.includes(item.value) };
    });
  };

  const getRoleOptions = () => {
    return roles
      .filter((item) =>
        isSuper
          ? item.type !== RbacRoleType.Owner
          : ![RbacRoleType.Owner, RbacRoleType.Admin].includes(item.type)
      )
      .map((item) => ({
        label: item.roleName,
        value: item.roleId,
      }));
  };

  const onOk = async () => {
    const formInfo = await form.validateFields();
    const currentRoles = await getSupportRoleList();

    if (members.some((item) => item.email?.split('@')[0] === formInfo.users)) {
      messageWarning('用户已存在');
      return false;
    }

    if (currentRoles) {
      const currentRoleIds = currentRoles.map((role) => role.roleId);
      const deletedRoleId = formInfo.roles.find(
        (roleId) => !currentRoleIds.includes(roleId)
      );
      const deletedRoleName = roles.find(
        (role) => role.roleId === deletedRoleId
      )?.roleName;
      if (deletedRoleName) {
        messageError(
          getFormatMessage('role_not_exist', {
            params: { roleName: deletedRoleName },
          })
        );
        return false;
      }
    }

    let newUsers: ISupportUserModelCreate[] = [formInfo.users].map(
      (formUserID) => {
        return {
          UserID: `${formUserID}@tencent.com`,
          UserName: `${formUserID}`,
        };
      }
    );

    const newRoles = formInfo.roles.map((roleId) => {
      const roleInfo = roles.find(
        (item) => item.roleId === roleId
      ) as IApiAdminRbacRole;
      return {
        ...roleInfo,
        userList: [
          ...roleInfo.userList,
          ...newUsers.map((item) => item.UserID),
        ],
      };
    });

    const editRes = await batchEditSupportRole(newRoles);

    let passport = '';
    if (isSingaporeArea) {
      passport = generateStrongPassword();
      newUsers = newUsers.map((userInfo) => {
        return { ...userInfo, Password: SparkMD5.hash(passport) };
      });
    }

    const createRes = await createSupportUsers(newUsers);
    if (editRes && createRes) {
      messageSuccess(getFormatMessage('successfully'));
      onSave();

      if (isSingaporeArea) {
        ModalWrap.show({
          title: '新成员账户信息',
          children: (
            <div>
              <div>账号: {newUsers[0].UserID}</div>
              <div>密码: {passport}</div>
              <div>
                可登录站点:
                <a href={location.origin}>{location.origin}</a>
              </div>
            </div>
          ),
          oKText: '复制',
          async onOk() {
            navigator.clipboard.writeText(
              `账号: ${newUsers[0].UserID}\n密码: ${passport}\n可登录站点: ${location.origin}`
            );
            messageSuccess(getFormatMessage('copy_success'));
            return false;
          },
        });
      }
      return true;
    }
    messageSuccess(getFormatMessage('failed'));
    return false;
  };

  const [form] = useForm<IInviteInfo>();

  const handleRoleChange = (values: number[]) => {
    const adminRole = roles.find((item) => item.type === RbacRoleType.Admin);
    if (!adminRole) {
      return;
    }

    const lastEditRole = roles.find(
      (role) => role.roleId === [...values].pop()
    );
    if (lastEditRole?.roleName === NOTICE_ADMIN_ROLE) {
      form.setFieldValue('roles', values);
      return;
    }

    if ([...values].pop() === adminRole.roleId) {
      const noticeRole = roles
        .filter((role) => values.includes(role.roleId))
        .find((role) => role.roleName === NOTICE_ADMIN_ROLE);
      const editRoleIds = noticeRole
        ? [adminRole.roleId, noticeRole.roleId]
        : [adminRole.roleId];
      form.setFieldValue('roles', editRoleIds);
    } else {
      form.setFieldValue(
        'roles',
        values.filter((id) => adminRole.roleId !== id)
      );
    }
  };

  const userOptions = getInviteUserOptions();
  return (
    <ConsoleDrawer
      title={getFormatMessage('invite_member')}
      onOk={onOk}
      okText={getFormatMessage('submit')}
      onClose={onCancel}
    >
      <ConsoleForm form={form}>
        <Form.Item
          label={getFormatMessage('members')}
          name="users"
          rules={[
            {
              required: true,
              validator: async (_: any, value = '') => {
                if (!/^[a-z_]+$/.test(value)) {
                  throw new Error('格式不正确');
                }
              },
            },
          ]}
        >
          {isSingaporeArea ? (
            <CommonInput
              suffix={'@tencent.com'}
              style={{ width: '100%' }}
              placeholder="请输入新成员的企业微信英文名称"
            />
          ) : (
            <CommonSelect
              showArrow={false}
              options={userOptions}
              placeholder={getFormatMessage('please_select_new_member')}
              dropdownStyle={{
                display: !searchText ? 'none' : '',
              }}
              dropdownRender={
                searchText && userOptions.length === 0
                  ? () => <span style={{ padding: 12 }}>未找到相关用户</span>
                  : undefined
              }
              onSearch={(text) => {
                searchStaffs(text);
              }}
              filterOption={() => true}
              showSearch={true}
            ></CommonSelect>
          )}
        </Form.Item>
        <Form.Item
          label={getFormatMessage('role')}
          name="roles"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <CommonMultiSelect
            onChange={handleRoleChange}
            options={getRoleOptions()}
            placeholder={getFormatMessage('please_select_the_role')}
          />
        </Form.Item>
      </ConsoleForm>
    </ConsoleDrawer>
  );
};
